import type { ReactNode } from "react";

import { cn } from "@/shared/lib/utils";

interface SettingsSectionProps {
  /** Anchor id without the `s-` prefix; used by the sticky sub-nav to scroll to this section. */
  id: string;
  /** Mono uppercase tag rendered above the title (e.g. "Apariencia", "Desarrolladores"). */
  eyebrow: string;
  title: string;
  sub?: string;
  /** Optional element rendered in the top-right of the section header (badge, stats, CTA, etc.). */
  headerRight?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Card section matching the design mock: eyebrow + title + sub + headerRight. */
export function SettingsSection({
  id,
  eyebrow,
  title,
  sub,
  headerRight,
  children,
  className,
}: SettingsSectionProps) {
  return (
    <section
      id={`s-${id}`}
      className={cn(
        "rounded-xl border border-border bg-card p-[22px] text-card-foreground shadow-xs",
        className,
      )}
    >
      {/* `headerRight` drops to its own line rather than squeezing the title. A badge or a stat strip does
          not shrink, so on a phone it was taking two thirds of the row and leaving the heading less than
          one word's width. Shared by every section here, so a change to this row shows up in all of them. */}
      <div className="mb-[18px] flex flex-wrap items-start gap-x-3.5 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
            {eyebrow}
          </div>
          <h2 className="m-0 text-base font-semibold tracking-[-0.01em] text-foreground">{title}</h2>
          {sub && <div className="mt-1 max-w-[600px] text-[12.5px] text-muted-foreground">{sub}</div>}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </div>
      {children}
    </section>
  );
}

export function SettingsDivider() {
  return <div className="my-1.5 h-px bg-border" />;
}
